import React, { useState } from 'react';
import {
    Dialog,
    DialogType,
    DialogFooter,
    PrimaryButton,
    DefaultButton,
    MessageBarType,
    Stack,
    Text
} from '@fluentui/react';
import { useAuth } from "react-oidc-context";
import {
    deleteCAEndUserAssignments,
    CAEndUserAssignmentSummary
} from "../../../services/AdminServices/CAEndUserAssignmentService";

interface CAEndUserAssignmentDeleteModalProps {
    resetDeleteDialogHide: (response: string) => void;
    deleteDialogHide: boolean;
    deleteUserEmail: string;
    deleteItem: CAEndUserAssignmentSummary | null;
    handleActionResponse: (response: string) => void;
}

function CAEndUserAssignmentDeleteModal({
    resetDeleteDialogHide,
    deleteDialogHide,
    deleteUserEmail,
    deleteItem,
    handleActionResponse
}: CAEndUserAssignmentDeleteModalProps) {
    const auth = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    const dialogContentProps = {
        type: DialogType.normal,
        title: 'Delete assignment',
    };

    const modalProps = React.useMemo(
        () => ({
            isBlocking: true,
        }),
        [],
    );

    const handleDelete = async () => {
        if (!deleteUserEmail) {
            return;
        }

        setIsLoading(true);
        try {
            await deleteCAEndUserAssignments(deleteUserEmail, auth.user?.id_token);
            handleActionResponse('deletetrue');
            resetDeleteDialogHide('close');
        } catch (error: any) {
            console.error('Error deleting assignment:', error);
            const errorMessage = error.message || 'An error occurred while deleting the assignment';
            handleActionResponse(`error: ${errorMessage}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        resetDeleteDialogHide('close');
    };

    return (
        <Dialog
            hidden={deleteDialogHide}
            onDismiss={handleCancel}
            dialogContentProps={dialogContentProps}
            modalProps={modalProps}
            maxWidth="560px"
        >
            <Stack tokens={{ childrenGap: 16 }}>
                <Text>
                    Are you sure you want to delete all legal basis assignments for user: <strong>{deleteUserEmail}</strong>?
                </Text>
                
                {deleteItem && (
                    <Text>
                        This will remove assignments for the following legal bases:
                    </Text>
                )}
                
                {deleteItem && (
                    <ul>
                        {deleteItem.assignedLegalBases.map(lb => (
                            <li key={lb.id}>{lb.name}</li>
                        ))}
                    </ul>
                )}
                
                <Text>
                    This action cannot be undone.
                </Text>
            </Stack>

            <DialogFooter>
                <PrimaryButton
                    text="Delete"
                    onClick={handleDelete}
                    disabled={isLoading}
                />
                <DefaultButton
                    text="Cancel"
                    onClick={handleCancel}
                    disabled={isLoading}
                />
            </DialogFooter>
        </Dialog>
    );
}

export default CAEndUserAssignmentDeleteModal;
